/**
 * Common item types used across the application
 */

export interface CustomizationOption {
  _id?: string | number;
  name: string;
  price: number;
}

export interface Customization {
  _id: string | number;
  name: string;
  selectedOption?: CustomizationOption;
  options?: CustomizationOption[];
  min_permitted: number;
  max_permitted: number;
}

/**
 * Base item interface with common properties
 */
export interface BaseItem {
  _id: string | number;
  name: string;
  price: number;
  description?: string;
  image_thumb_url?: string;
  status?: 'ACTIVE' | 'INACTIVE';
}

/**
 * Menu item as returned from the API
 */
export interface MenuItem extends BaseItem {
  _id: number;
  item_type_id?: number;
  item_type_name?: string;
  created_at?: string;
  updated_at?: string;
  discount_config?: {
    type: 'VALUE' | 'PERCENTAGE';
    value: number;
  };
  discounted_price?: number;
  emptyCustomization?: boolean;
  note?: string;
}

/**
 * Cart item with quantity and customizations
 */
export interface CartItem extends BaseItem {
  _id: string;
  quantity: number;
  customizations?: Customization[];
  note?: string;
}

/**
 * Converts a MenuItem to CartItem format
 */
export const menuItemToCartItem = (
  menuItem: MenuItem,
  quantity = 1
): CartItem => ({
  _id: String(menuItem._id),
  name: menuItem.name,
  price: menuItem.price,
  quantity,
  description: menuItem.description ?? '',
  image_thumb_url: menuItem.image_thumb_url ?? '',
  status: menuItem.status ?? 'ACTIVE',
  note: menuItem.note,
});

/**
 * Converts a CartItem to MenuItem format
 * Preserves customizations for editing existing cart items
 */
export const cartItemToMenuItem = (cartItem: CartItem): MenuItem => {
  // Create base MenuItem
  const menuItem: MenuItem = {
    _id: Number(cartItem._id),
    name: cartItem.name,
    price: cartItem.price,
    description: cartItem.description ?? '',
    image_thumb_url: cartItem.image_thumb_url ?? '',
    status: cartItem.status ?? 'ACTIVE',
    discounted_price: cartItem.price,
    note: cartItem.note,
  };

  // Add customizations as a property to pass to store item sheet
  // This ensures we maintain selected options when editing
  if (cartItem.customizations && cartItem.customizations.length > 0) {
    (menuItem as any).customizations = cartItem.customizations;
  }

  // Always preserve cartItemId and quantity when converting from cart item
  if ((cartItem as any).cartItemId) {
    (menuItem as any).cartItemId = (cartItem as any).cartItemId;
  }
  (menuItem as any).quantity = cartItem.quantity;

  return menuItem;
};
